import { ComponentProps, FC } from 'react';

import { cn } from '@/lib/utils';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { NavMain } from '@/components/nav-main';
import { WorkspaceSwitcher } from '@/components/workspace-switcher';

import LogoWithText from '@/assets/images/view-metrics-logo-text.svg';
import { APP_ROUTE } from '@/constants';
import { useNavigate, useLocation } from 'react-router-dom';
import ActivePlanCard from '@/views/settings/billing/components/active-plan-card';
import { useRecoilValue } from 'recoil';
import { QuickStartState } from '@/states';
import {
  ClientContainer,
  PagesContainer,
  TeamContainer,
  UserContainer,
} from '@/providers';
import { getSubscriptionName } from '@/views/settings/billing/utils';
import { useSession } from '@/states/common';
import ActivationFlowSidebar from '@/components/activation-flow-sidebar';

// This is sample data.
const data = {
  teams: [
    {
      name: 'Acme Inc',
      logo: ({ className }: { className?: string }) => (
        <i className={cn('ti ti-buildings text-2xl', className)} />
      ),
      plan: 'Enterprise',
    },
    {
      name: 'Acme Corp.',
      logo: ({ className }: { className?: string }) => (
        <i className={cn('ti ti-buildings text-2xl', className)} />
      ),
      plan: 'Startup',
    },
  ],
  navMain: [
    {
      title: 'Dashboard',
      url: `/${APP_ROUTE.HOME}`,
      icon: ({ className }: { className?: string }) => (
        <i
          className={cn(
            'ti ti-layout-dashboard text-xl text-grayscale-600 font-normal',
            className
          )}
        />
      ),
    },
    {
      title: 'Connections',
      icon: ({ className }: { className?: string }) => (
        <i
          className={cn(
            'ti ti-plug-connected text-xl text-grayscale-600 font-normal',
            className
          )}
        />
      ),
      pathMatch: `/${APP_ROUTE.CONNECTIONS}/*`,
      items: [
        {
          title: 'Data Sources',
          url: `/${APP_ROUTE.CONNECTIONS}/${APP_ROUTE.DATA_SOURCES}`,
        },
        {
          title: 'Destination',
          url: `/${APP_ROUTE.CONNECTIONS}/${APP_ROUTE.DESTINATION}`,
        },
      ],
    },
    {
      title: 'Analytics',
      icon: ({ className }: { className?: string }) => (
        <i
          className={cn(
            'ti ti-chart-pie text-xl text-grayscale-600 font-normal',
            className
          )}
        />
      ),
      url: `/${APP_ROUTE.ANALYTICS}`,
    },
    {
      title: 'Embedded Charts',
      url: `/${APP_ROUTE.EMBED}/${APP_ROUTE.CHARTS}`,
      icon: ({ className }: { className?: string }) => (
        <i
          className={cn(
            'ti ti-code-circle text-xl text-grayscale-600 font-normal',
            className
          )}
        />
      ),
    },
    {
      title: 'Reporting',
      pathMatch: `/${APP_ROUTE.REPORTING}/*`,
      icon: ({ className }: { className?: string }) => (
        <i
          className={cn(
            'ti ti-report text-xl text-grayscale-600 font-normal',
            className
          )}
        />
      ),
      items: [
        {
          title: 'Reports',
          url: `/${APP_ROUTE.REPORTING}/${APP_ROUTE.REPORTS}`,
        },
        {
          title: 'Templates',
          url: `/${APP_ROUTE.REPORTING}/${APP_ROUTE.TEMPLATES}`,
        },
      ],
    },
    {
      title: 'Settings',
      pathMatch: `/${APP_ROUTE.SETTINGS}/*`,
      icon: ({ className }: { className?: string }) => (
        <i
          className={cn(
            'ti ti-settings-2 text-xl text-grayscale-600 font-normal',
            className
          )}
        />
      ),
      items: [
        {
          title: 'Profile',
          url: `/${APP_ROUTE.SETTINGS}/${APP_ROUTE.PROFILE}`,
        },
        {
          title: 'Account',
          url: `/${APP_ROUTE.SETTINGS}/${APP_ROUTE.ACCOUNT}`,
        },
        {
          title: 'Workspace',
          url: `/${APP_ROUTE.SETTINGS}/${APP_ROUTE.WORKSPACE}`,
        },
        {
          title: 'Team',
          url: `/${APP_ROUTE.SETTINGS}/${APP_ROUTE.TEAM}`,
        },
        {
          title: 'Billing',
          url: `/${APP_ROUTE.SETTINGS}/${APP_ROUTE.BILLING}`,
        },
      ],
    },
  ],
};

const LogoWithToggle = () => {
  const { open } = useSidebar();
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        {open ? (
          <div className="flex items-center justify-between h-[72px]">
            <img
              src={LogoWithText}
              alt="ViewMetrics's logo with text"
              className="h-8"
            />
            <SidebarTrigger />
          </div>
        ) : (
          <div className="flex items-center justify-center w-full pt-2">
            <SidebarTrigger
              className="bg-white shadow-sm"
              iconClassName="text-grayscale-400"
            />
          </div>
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  );
};

const PlanProgress = () => {
  const { state: userState } = UserContainer.useContainer();
  const { paymentPlans } = userState;
  const { state: pagesState } = PagesContainer.useContainer();
  const { state: clientState } = ClientContainer.useContainer();
  const { state: teamState } = TeamContainer.useContainer();
  const { data: session } = useSession();
  const { billing: billingInfo } = session;
  const navigate = useNavigate();

  const planType =
    userState?.business?.billing?.paddleSubscription?.planName
      ?.toLowerCase()
      ?.trim() || 'free';

  const teamAmount = teamState?.members?.length;

  const currentPriceId = billingInfo?.paddleSubscription?.subscriptionPriceId;
  const activePlan = [
    ...(paymentPlans?.year || []),
    ...(paymentPlans?.month || []),
  ].find(plan => plan.priceId === currentPriceId);

  const paddleBillingInfo = userState?.business?.billing;

  const isLegacyUser = paddleBillingInfo?.isLegacyUser;
  const isAppSumoCustomer = billingInfo?.isAppSumoCustomer;
  const isTenCodePurchaser = billingInfo?.isTenCodePurchaser;

  let allowedCounts;
  if (isLegacyUser) {
    allowedCounts = {
      datasources: billingInfo?.sources || 3,
      workspace: billingInfo?.workspaces ? 10 : 1,
      user: billingInfo?.team ? 10 : 1,
    };

    if (isAppSumoCustomer || isTenCodePurchaser) {
      allowedCounts = {
        datasources:
          billingInfo?.additionalSources + billingInfo?.sources || 20,
        workspace: true,
        user: billingInfo?.additionalUsers + 1 || 3,
      };
    }
  } else {
    allowedCounts = activePlan?.requirements || {
      datasources: paddleBillingInfo?.sources || 3,
      workspace: paddleBillingInfo?.workspaces ? 10 : 1,
      user: paddleBillingInfo?.team ? 10 : 1,
    };
  }

  const datasourceCount = pagesState.pages?.filter(page => page.clientId);
  const datasourcesLength = `${datasourceCount?.length || 0}/${
    allowedCounts.datasources
  }`;
  const workspaceCount = `${userState.businessOptions?.length || 0}/${
    allowedCounts.workspace === true ? '∞' : allowedCounts.workspace
  }`;

  // Calculate user count based on plan type
  const isTeamPlan = planType === 'team';
  const userCountValue = teamAmount || 1;
  // Only count primary user for non-team plans
  const userCount = `${userCountValue}/${allowedCounts.user}`;

  if (isLegacyUser) {
    const planTypeForLegacyUser = getSubscriptionName(
      billingInfo?.status,
      billingInfo?.subscriptionName
    );

    return (
      <ActivePlanCard
        type={
          isLegacyUser
            ? (planTypeForLegacyUser?.plan?.toLowerCase() as
                | 'free'
                | 'starter'
                | 'professional'
                | 'team')
            : (planType as 'free' | 'starter' | 'professional' | 'team')
        }
        dataSource={datasourcesLength}
        workspace={workspaceCount}
        user={userCount}
        onUpgrade={() => {
          navigate(
            `/ws/${clientState.selectedClient?.id}/${APP_ROUTE.SETTINGS}/${APP_ROUTE.BILLING}`
          );
        }}
      />
    );
  }

  return (
    <ActivePlanCard
      type={planType as 'free' | 'starter' | 'professional' | 'team'}
      dataSource={datasourcesLength}
      workspace={workspaceCount}
      user={userCount}
      onUpgrade={() => {
        navigate(
          `/ws/${clientState.selectedClient?.id}/${APP_ROUTE.SETTINGS}/${APP_ROUTE.BILLING}`
        );
      }}
    />
  );
};

const SidebarFooterContent = () => {
  const { open, isMobile, isTablet } = useSidebar();
  const quickStart = useRecoilValue(QuickStartState);
  const { state: userState } = UserContainer.useContainer();
  const location = useLocation();

  // Check if we're in the onboarding context to prevent API failures
  const isOnboarding = location.pathname.includes('onboarding');

  const completedSteps = Object.values(quickStart).filter(
    step => step === 'completed'
  ).length;
  const totalSteps = Object.keys(quickStart).length;
  const isActivationComplete = completedSteps === totalSteps;

  const shouldShowActivationFlow = userState.business?.activationFlow === false;

  // Always show footer content on mobile/tablet (expanded), on desktop only when open
  if (!open && !isMobile && !isTablet) return null;

  return (
    <div className="space-y-3">
      {shouldShowActivationFlow && !isActivationComplete && !isOnboarding ? (
        <ActivationFlowSidebar />
      ) : (
        !isOnboarding && <PlanProgress />
      )}
    </div>
  );
};

export const AppSidebar: FC<ComponentProps<typeof Sidebar>> = props => (
  <Sidebar collapsible="icon" {...props}>
    <SidebarHeader className="pt-0 gap-0">
      <LogoWithToggle />
      <WorkspaceSwitcher workspaces={data.teams} />
    </SidebarHeader>

    <SidebarContent>
      <NavMain items={data.navMain} />
    </SidebarContent>

    <SidebarFooter>
      <SidebarFooterContent />
    </SidebarFooter>

    <SidebarRail />
  </Sidebar>
);
